import './App.css';
import Sidebar from './Sidebar';
import 'bootstrap/dist/css/bootstrap.min.css';
import { useCookies } from 'react-cookie';
import './Main.css';
import Background from './Background';
import { useState, useEffect } from 'react';
import { Link } from "react-router-dom";

function TransactionList() {
    const [cookies, setCookie] = useCookies(['user']);
    const [transactionList, setTransactionList] = useState([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [lastPage, setLastPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    const getTransactionList = () => {
        setIsLoading(true);
        const params = new URLSearchParams({
            page: page,
            per_page: pageSize
        });

        // Add search parameter if searchTerm is not empty
        if (searchTerm.trim() !== '') {
            params.append('search', searchTerm.trim());
        }

        fetch(`https://api.connecker.com/api/transactions?${params}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            }
        }).then(res => {
            setIsLoading(false);
            if (!res.ok) {
                throw Error('Could not fetch');
            }
            return res.json();
        }).then(data => {
            setIsLoading(false);
            if (data.success) {
                setTransactionList(data.data.data);
                setPage(data.data.current_page);
                setLastPage(data.data.last_page);
            } else {
                setTransactionList([]);
            }
        }).catch(error => {
            setIsLoading(false);
            console.error('Error fetching transactions:', error);
            setTransactionList([]);
        });
    }

    const nextPage = () => {
        setPage(page + 1);
    }

    const previousPage = () => {
        setPage(page - 1);
    }

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit'
        });
    }

    const updateTransactionStatus = (transactionId, newStatus) => {
        const adminToken = cookies.token; // Assuming token is stored in cookies
        
        fetch('https://api.connecker.com/api/transactions/update-status', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${adminToken}`
            },
            body: JSON.stringify({
                transaction_id: transactionId,
                status: newStatus
            })
        })
        .then(res => {
            if (!res.ok) {
                throw Error('Could not update status');
            }
            return res.json();
        })
        .then(data => {
            if (data.success) {
                // Update the transaction in local state
                setTransactionList(prev => 
                    prev.map(transaction => 
                        transaction.id === transactionId 
                            ? { ...transaction, status: newStatus }
                            : transaction
                    )
                );
            } else {
                alert('Failed to update transaction status');
            }
        })
        .catch(error => {
            console.error('Error updating status:', error);
            alert('Error updating transaction status');
        });
    };

    const getStatusDropdown = (transaction) => {
        const statuses = ['paid', 'initiated', 'pending', 'cancelled'];
        
        const getStatusColor = (status) => {
            switch (status) {
                case 'paid':
                    return '#28a745';
                case 'pending':
                    return '#ffc107';
                case 'initiated':
                    return '#17a2b8';
                case 'cancelled':
                    return '#dc3545';
                default:
                    return '#6c757d';
            }
        };

        return (
            <select
                value={transaction.status}
                onChange={(e) => updateTransactionStatus(transaction.id, e.target.value)}
                style={{
                    padding: "4px 8px",
                    backgroundColor: getStatusColor(transaction.status),
                    color: 'white',
                    border: 'none',
                    borderRadius: "8px",
                    fontSize: "12px",
                    fontWeight: "500",
                    textTransform: "uppercase",
                    cursor: 'pointer',
                    outline: 'none'
                }}
            >
                {statuses.map(status => (
                    <option key={status} value={status} style={{ backgroundColor: 'white', color: '#333' }}>
                        {status}
                    </option>
                ))}
            </select>
        );
    }

    // Apply only status filter client-side (search is handled server-side)
    const filteredTransactions = transactionList.filter(transaction => {
        const matchesStatus = statusFilter === "" || transaction.status === statusFilter;
        return matchesStatus;
    });

    useEffect(() => {
        const abortController = new AbortController();
        getTransactionList();
    }, [page, pageSize]);

    // Debounce search term changes
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setPage(1); // Reset to first page when searching
            getTransactionList();
        }, 500); // 500ms delay

        return () => clearTimeout(timeoutId);
    }, [searchTerm]);

    return (
        <div className="content">
            <div className="row pb-2">
                <div className="col-2 col-sm-2 col-md-2 col-lg-2 col-xl-2 col-xxl-2">
                    <Sidebar></Sidebar>
                </div>
                <div className="col-10 col-sm-10 col-md-10 col-lg-10 col-xl-10 col-xxl-10" style={{ paddingLeft: "40px" }}>
                    <Background></Background>
                    <h4 className="heading" style={{ color: "white", marginTop: "50px" }}>Transaction List</h4>

                    {/* Filter Controls */}
                    <div className="card mb-3" style={{ borderColor: "white", padding: "20px" }}>
                        <div className="row align-items-center">
                            <div className="col-md-2">
                                <div className="d-flex align-items-center">
                                    <span className="me-2" style={{ fontSize: "14px", color: "#666" }}>Show</span>
                                    <select
                                        name="entries"
                                        id="entries"
                                        className="form-select form-select-sm"
                                        style={{ width: "70px", fontSize: "14px" }}
                                        value={pageSize}
                                        onChange={({ target: { value } }) => setPageSize(value)}
                                    >
                                        <option value="10">10</option>
                                        <option value="20">20</option>
                                        <option value="50">50</option>
                                        <option value="100">100</option>
                                    </select>
                                    <span className="ms-2" style={{ fontSize: "14px", color: "#666" }}>entries</span>
                                </div>
                            </div>
                            <div className="col-md-2">
                                <div className="d-flex align-items-center">
                                    <span className="me-2" style={{ fontSize: "14px", color: "#666" }}>Status</span>
                                    <select
                                        name="status"
                                        id="status"
                                        className="form-select form-select-sm"
                                        style={{ fontSize: "14px" }}
                                        value={statusFilter}
                                        onChange={({ target: { value } }) => setStatusFilter(value)}
                                    >
                                        <option value="">All</option>
                                        <option value="paid">Paid</option>
                                        <option value="initiated">Initiated</option>
                                        <option value="pending">Pending</option>
                                        <option value="cancelled">Cancelled</option>
                                    </select>
                                </div>
                            </div>
                            <div className="col-md-5"></div>
                            <div className="col-md-3">
                                <div className="d-flex align-items-center justify-content-end">
                                    <span className="me-2" style={{ fontSize: "14px", color: "#666" }}>Search:</span>
                                    <input
                                        type="text"
                                        id="listSearch"
                                        className="form-control form-control-sm"
                                        style={{ width: "150px", fontSize: "14px" }}
                                        placeholder="Search..."
                                        value={searchTerm}
                                        onChange={({ target: { value } }) => setSearchTerm(value)}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="card" style={{ borderColor: "white" }}>
                        <div className="table-responsive">
                            <table style={{ width: "100%" }} className="table table-striped">
                                <thead style={{ backgroundColor: "#f8f9fa" }}>
                                    <tr style={{ borderBottom: "2px solid #dee2e6" }}>
                                        <th style={{ color: "#666", fontWeight: "600", fontSize: "14px", padding: "12px 8px" }}>#</th>
                                        <th style={{ color: "#666", fontWeight: "600", fontSize: "14px", padding: "12px 8px" }}>REF COMMAND</th>
                                        <th style={{ color: "#666", fontWeight: "600", fontSize: "14px", padding: "12px 8px" }}>CUSTOMER</th>
                                        <th style={{ color: "#666", fontWeight: "600", fontSize: "14px", padding: "12px 8px" }}>PROPERTY</th>
                                        <th style={{ color: "#666", fontWeight: "600", fontSize: "14px", padding: "12px 8px" }}>AMOUNT</th>
                                        <th style={{ color: "#666", fontWeight: "600", fontSize: "14px", padding: "12px 8px" }}>STATUS</th>
                                        <th style={{ color: "#666", fontWeight: "600", fontSize: "14px", padding: "12px 8px" }}>DATE</th>
                                        <th style={{ color: "#666", fontWeight: "600", fontSize: "14px", padding: "12px 8px" }}>ACTIONS</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {isLoading ? (
                                        <tr>
                                            <td colSpan="8" style={{ textAlign: "center", padding: "20px" }}>
                                                <span style={{ color: "#666" }}>Loading...</span>
                                            </td>
                                        </tr>
                                    ) : filteredTransactions.length > 0 ? (
                                        filteredTransactions.map((transaction, i) => (
                                            <tr key={transaction.id} style={{ borderBottom: "1px solid #eee" }}>
                                                <td style={{ padding: "12px 8px", fontSize: "14px", color: "#333" }}>
                                                    {(page - 1) * pageSize + i + 1}
                                                </td>
                                                <td style={{ padding: "12px 8px", fontSize: "12px", fontFamily: "monospace", color: "#333" }}>
                                                    {transaction.ref_command}
                                                </td>
                                                <td style={{ padding: "12px 8px", fontSize: "14px", color: "#333" }}>
                                                    <div>
                                                        <div style={{ fontWeight: "500" }}>
                                                            {transaction.user?.middle_name && transaction.user?.middle_name !== "null" ?
                                                                `${transaction.user?.first_name} ${transaction.user?.middle_name} ${transaction.user?.last_name}` :
                                                                `${transaction.user?.first_name || ''} ${transaction.user?.last_name || ''}`
                                                            }
                                                        </div>
                                                        <div style={{ fontSize: "12px", color: "#666" }}>
                                                            {transaction.user?.email}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td style={{ padding: "12px 8px", fontSize: "14px", color: "#333" }}>
                                                    <div>
                                                        <div style={{ fontWeight: "500" }}>{transaction.item_name}</div>
                                                        <div style={{ fontSize: "12px", color: "#666" }}>
                                                            {transaction.property?.region}, {transaction.property?.town}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td style={{ padding: "12px 8px", fontSize: "14px", fontWeight: "600", color: "#333" }}>
                                                    {parseFloat(transaction.amount).toLocaleString()} {transaction.currency}
                                                </td>
                                                <td style={{ padding: "12px 8px" }}>
                                                    {getStatusDropdown(transaction)}
                                                </td>
                                                <td style={{ padding: "12px 8px", fontSize: "14px", color: "#666" }}>
                                                    {formatDate(transaction.created_at)}
                                                </td>
                                                <td style={{ padding: "12px 8px" }}>
                                                    <Link
                                                        to={`/transaction/${transaction.id}`}
                                                        style={{
                                                            color: "#007bff",
                                                            textDecoration: "none",
                                                            fontSize: "14px",
                                                            fontWeight: "500"
                                                        }}
                                                    >
                                                        View
                                                    </Link>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="8" style={{ textAlign: "center", padding: "20px" }}>
                                                <span style={{ color: "#666" }}>No transactions found</span>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Pagination */}
                    <div className="row d-flex justify-content-between">
                        <div className="col-md-6 col-lg-6 col-xl-6 col-xxl-6">
                            {filteredTransactions.length > 0 && (
                                <span style={{ color: "#666", fontSize: "14px" }}>
                                    Showing {((page - 1) * pageSize) + 1} to {Math.min(page * pageSize, filteredTransactions.length)} of {filteredTransactions.length} entries
                                </span>
                            )}
                        </div>
                        <div className="col-md-6 col-lg-6 col-xl-6 col-xxl-6">
                            <div>
                                {((page > 1) && transactionList.length > 0) ? 
                                    <button type="button" onClick={previousPage} className="btn btn-outline-secondary me-2">Previous</button> : 
                                    <div></div>
                                }
                                {((lastPage > page) && transactionList.length > 0) ? 
                                    <button type="button" style={{ color: "#FF4500" }} onClick={nextPage} className="btn btn-outline-secondary">Next</button> : 
                                    <div></div>
                                }
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default TransactionList;